import { Button, CircularProgress, Grid, LinearProgress } from "@mui/material";
import { observer } from "mobx-react-lite";
import Script from "next/script";
import React from "react";
import { OrderAddress } from "../../requests/OrderRequests";
import PaymentProcessingStore from "../../stores/PaymentProcessingStore";
import ThemeStore from "../../stores/ThemeStore";
import { getErrorMessage } from "../../utils/helpers";
import { square } from "../../utils/square";
import toast from "../../utils/toast";

type Props = {
    clientKey: string | number;
    totalCost: number;
    onPostalCodeChanged: (postalCode: string) => void;
    onSquareSubmit: (nonce: string, verificationToken?: string) => void;
    address: OrderAddress;
    loading: boolean;
    currency: string;
    gatewayId: number | undefined;
    overridePurchaseText?: string;
    loadingOnChange?: (l: boolean) => void;
};

const SquarePaymentForm = observer(
    ({
        clientKey,
        totalCost,
        onPostalCodeChanged,
        onSquareSubmit,
        address,
        currency,
        loading: propLoading,
        gatewayId,
        overridePurchaseText,
        loadingOnChange,
    }: Props) => {
        const [cardBrand, setCardBrand] = React.useState("");
        const [googlePay, setGooglePay] = React.useState(false);
        const [applePay, setApplePay] = React.useState(false);
        const [masterpass, setMasterpass] = React.useState(false);
        const [loading, setLoading] = React.useState(false);
        const [initialLoad, setInitialLoad] = React.useState(true);

        const _payments = React.useRef<any>(null);
        const _card = React.useRef<any>(null);
        const _applePay = React.useRef<any>(null);
        const _googlePay = React.useRef<any>(null);

        async function initializeCard(payments: any) {
            const card = await payments.card({
                style: {
                    ".input-container": {
                        borderColor: "transparent",
                        borderRadius: "6px",
                    },
                    ".input-container.is-focus": {
                        borderColor: ThemeStore.primaryColor,
                    },
                    ".input-container.is-error": {
                        borderColor: "#ff1600",
                    },
                    ".message-text": {
                        color:
                            ThemeStore.mode === "dark" ? "#FFFFFF" : "#000000",
                    },
                    ".message-icon": {
                        color:
                            ThemeStore.mode === "dark" ? "#FFFFFF" : "#000000",
                    },
                    ".message-text.is-error": {
                        color: "#ff1600",
                    },
                    ".message-icon.is-error": {
                        color: "#ff1600",
                    },
                    input: {
                        backgroundColor:
                            ThemeStore.mode === "dark" ? "#2D2D2D" : "#FFFFFF",
                        color:
                            ThemeStore.mode === "dark" ? "#FFFFFF" : "#000000",
                        fontFamily: "helvetica neue, sans-serif",
                    },
                    "input::placeholder": {
                        color: "#999999",
                    },
                    "input.is-error": {
                        color: "#ff1600",
                    },
                    "@media screen and (max-width: 600px)": {
                        input: {
                            fontSize: "12px",
                        },
                    },
                },
            });
            await card.attach("#card-container");

            return card;
        }

        function buildPaymentRequest(payments: any) {
            return payments.paymentRequest({
                countryCode: currency.substring(0, 2),
                currencyCode: currency,
                total: {
                    amount: (totalCost / 100).toFixed(2),
                    label: "Total",
                },
            });
        }

        async function initializeApplePay(payments: any) {
            const paymentRequest = buildPaymentRequest(payments);
            const applePay = await payments.applePay(paymentRequest);
            return applePay;
        }

        async function initializeGooglePay(payments: any) {
            const paymentRequest = buildPaymentRequest(payments);
            const googlePay = await payments.googlePay(paymentRequest);
            await googlePay.attach("#google-pay-button", {
                buttonSizeMode: "fill",
            });

            return googlePay;
        }

        async function tokenize(paymentMethod: any): Promise<string> {
            const tokenResult = await paymentMethod.tokenize();
            if (tokenResult.status === "OK") {
                return tokenResult.token;
            } else {
                let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
                if (tokenResult.errors) {
                    errorMessage += ` and errors: ${JSON.stringify(
                        tokenResult.errors,
                    )}`;
                }

                throw new Error(errorMessage);
            }
        }

        async function verifyBuyer(payments: any, token: string) {
            const verificationDetails = {
                amount: (totalCost / 100).toFixed(2),
                /* collected from the buyer */
                billingContact: {
                    addressLines: [address.address, address.address2 ?? ""],
                    familyName: address.lastName,
                    givenName: address.firstName,
                    email: address.email,
                    // countryCode: address.countryName ?? "",
                    phone: address.phone,
                    city: address.city,
                },
                currencyCode: currency,
                intent: "CHARGE",
            };

            const verificationResults = await payments.verifyBuyer(
                token,
                verificationDetails,
            );
            return verificationResults.token;
        }

        // status is either SUCCESS or FAILURE;
        function displayPaymentResults(status: "SUCCESS" | "FAILURE") {
            const statusContainer = document.getElementById(
                "payment-status-container",
            )!;
            if (status === "SUCCESS") {
                statusContainer.classList.remove("is-failure");
                statusContainer.classList.add("is-success");
            } else {
                statusContainer.classList.remove("is-success");
                statusContainer.classList.add("is-failure");
            }

            statusContainer.style.visibility = "visible";
        }

        async function requestCardNonce(e: any, paymentMethod: any) {
            e.preventDefault();

            setLoading(true);
            try {
                const token = await tokenize(paymentMethod);
                let verificationToken: string | undefined = undefined;
                if (gatewayId === 6) {
                    verificationToken = await verifyBuyer(
                        _payments.current,
                        token,
                    );
                }
                onSquareSubmit(token, verificationToken);
            } catch (e: any) {
                console.error(e.message);
            } finally {
                setLoading(false);
            }
        }

        async function refresh() {
            const square = (window as any).Square;
            if (!square) {
                throw new Error("Square.js failed to load properly");
            }

            try {
                _payments.current = square.payments(
                    PaymentProcessingStore.clientConfiguration
                        .squareApplicationId,
                    PaymentProcessingStore.clientConfiguration.squareLocationId,
                );
            } catch (e) {
                console.log(e);
                const statusContainer = document.getElementById(
                    "payment-status-container",
                )!;
                statusContainer.className = "missing-credentials";
                statusContainer.style.visibility = "visible";
                return;
            }

            try {
                if (_card.current) {
                    await _card.current.destroy();
                    _card.current = null;
                }
                _card.current = await initializeCard(_payments.current);
                // this.card.addEventListener("postalCodeChanged", event => {
                //     onPostalCodeChanged(event.detail.postalCodeValue);
                // });
                _card.current.addEventListener(
                    "cardBrandChanged",
                    (event: any) => {
                        setCardBrand(event.detail.cardBrand);
                    },
                );
            } catch (e) {
                console.error("Initializing Card failed", e);
                return;
            }

            try {
                if (_applePay.current) {
                    _applePay.current.destroy();
                    _applePay.current = null;
                }
                _applePay.current = await initializeApplePay(_payments.current);
            } catch (e) {
                console.error("Failed to initialize apple pay: ", e);
            }

            if (_applePay.current) {
                setApplePay(true);
            }

            try {
                if (_googlePay.current) {
                    _googlePay.current.destroy();
                    _googlePay.current = null;
                }
                _googlePay.current = await initializeGooglePay(
                    _payments.current,
                );
            } catch (e) {
                console.error("Failed to initialize google pay: ", e);
            }

            if (_googlePay.current) {
                setGooglePay(true);
            }
        }

        React.useEffect(() => {
            if (!initialLoad) {
                refresh().catch((err) => toast.error(getErrorMessage(err)));
            }
        }, [totalCost]);

        React.useEffect(() => {
            if (loadingOnChange) {
                loadingOnChange(loading);
            }
        }, [loading]);

        React.useEffect(() => {
            square.initialize(
                () => {
                    refresh()
                        .catch((err) => toast.error(getErrorMessage(err)))
                        .finally(() => {
                            setInitialLoad(false);
                            console.log("Done initial square load");
                        });
                },
                () => {
                    toast.error("Failed to initialize Payment Processor.");
                },
            );
        }, []);

        return (
            <div>
                <div id="form-container">
                    <form
                        id="payment-form"
                        onSubmit={(e) => requestCardNonce(e, _card.current)}
                    >
                        {applePay && (
                            <div
                                id="apple-pay-button"
                                style={{ marginBottom: "10px" }}
                                onClick={(e) =>
                                    requestCardNonce(e, _applePay.current)
                                }
                            ></div>
                        )}
                        <div
                            id="google-pay-button"
                            style={{
                                marginBottom: "10px",
                            }}
                            onClick={(e) =>
                                requestCardNonce(e, _googlePay.current)
                            }
                        ></div>
                        <div id="card-container"></div>
                        <p id="error" />
                        {initialLoad && <LinearProgress sx={{ my: 2 }} />}
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    disabled={loading || propLoading}
                                    variant="contained"
                                    fullWidth
                                >
                                    {(loading || propLoading) && (
                                        <CircularProgress size={25} />
                                    )}

                                    {!(loading || propLoading) &&
                                        (overridePurchaseText ??
                                            "Complete Purchase")}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </div>
            </div>
        );
    },
);

export default SquarePaymentForm;
